import { memo, useLayoutEffect, useState } from 'react';
import history from '@history';
import { useDispatch, useSelector } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Switch from '@mui/material/Switch';
import FormLabel from '@mui/material/FormLabel';
import FormControlLabel from '@mui/material/FormControlLabel';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import WdPageSimple from '@main/core/WdPageSimple';
import {
  ButtonWithLoader,
  ComposedHeader,
  FormCardLayout,
  ImagePicker,
  ListCheckers,
  MetadataExpandForm,
} from '@main/components';
import { showMessage } from 'app/store/core/messageSlice';
import { getScopes, storeAdmin } from 'app/api/admin';
import _ from 'lodash';
import { useWithApiValidation } from '@main/hooks/useWithApiValidation';
import { validateEmail, validatePhone } from 'app/api/validations';
import { uploadImageFile } from 'app/api/image';
import { useRequest } from '@main/hooks';
import { generateHexColor } from 'app/utils/generators';
import { Divider, Grid } from '@mui/material';
import { PhoneCodeSelect } from 'src/app/components';
import { createSchema } from '../Validators/Create';
import { imageDefaults } from '../Defaults/image';
import { createDefaults } from '../Defaults/create';

const Root = styled(WdPageSimple)(({ theme }) => ({
  backgroundColor: '#FFF',
  '& .WdPageSimple-header': {},
  '& .WdPageSimple-content': {},
}));

const PermissionsCollection = memo(({ field, errors, catalogScopes, ...props }) => {
  return (
    <Grid {...props} container spacing={1} className="mb-8 flex justify-center items-start">
      <Grid item xs={12}>
        <ListCheckers
          {...field}
          horizontal
          selectAllChecker
          selectAllLabel="Agregar todos los permisos"
          labelPlacement="end"
          fieldset
          onChange={field.onChange}
          values={field.value}
          className="pt-0 w-full flex"
          innerClassName="flex justify-center flex-row"
          data={[]}
        />
      </Grid>
      {catalogScopes.map((catalog, index) => {
        const arrCatalog = Object.keys(catalog.scopes).map((key) => ({
          value: key,
          label: catalog.scopes[key],
        }));
        return (
          <Grid
            item
            key={`label_${catalog?.label}_${index}`}
            xs={12}
            md={6}
            className="flex justify-center items-center flex-col"
          >
            <ListCheckers
              {...field}
              fieldset
              onChange={field.onChange}
              values={field.value}
              className="w-full flex"
              title={catalog?.label}
              description={catalog?.description}
              error={errors}
              data={arrCatalog}
            />
          </Grid>
        );
      })}
    </Grid>
  );
});

const Create = () => {
  const Dispatch = useDispatch();
  const { config } = useSelector((state) => state.config);
  const { api } = config;
  const [metadata, setMetadata] = useState({});
  const [metadataError, setMetadataError] = useState(false);
  const [catalogScopes, setCatalogScopes] = useState([]);
  const [avatar, setAvatar] = useState(undefined);

  // ----- Inicializacion de validadores de formulario
  const { control, formState, handleSubmit, watch } = useForm({
    mode: 'onChange',
    defaultValues: createDefaults,
    resolver: yupResolver(createSchema),
  });
  const { isValid, dirtyFields, errors } = formState;
  const handleOnError = async (message) => {
    Dispatch(showMessage({ message }));
  };
  // ------ Request Store Admin ------
  const { makeRequest: tryStore, isLoading } = useRequest(storeAdmin, {
    onError: handleOnError,
  });

  // ------- Request Get Scopes Admin -----------
  const { makeRequest: tryGetScopes, response: responseGetScopes } = useRequest(getScopes, {
    onError: handleOnError,
  });

  // ------- Watchers scopes ----------
  useLayoutEffect(() => {
    tryGetScopes();
  }, []);

  useLayoutEffect(() => {
    if (responseGetScopes === undefined) return;
    console.log(responseGetScopes);
    setCatalogScopes(responseGetScopes);
  }, [responseGetScopes]);

  // ----- Handler get image -------
  const handleLoadImage = (image) => {
    setAvatar(image);
  };

  const handleUpdate = (form) => {
    const background = generateHexColor();
    const builtForm = {
      ...form,
      avatar,
      metadata,
      phoneCode: form.phoneCode || '+52',
      background: `#${background}`,
    };
    console.log(JSON.stringify(builtForm, null, 3));
    tryStore(builtForm).then(({ isOk }) => {
      if (isOk) {
        Dispatch(
          showMessage({
            message: 'Administrador agregado correctamente',
            variant: 'success',
          })
        );
        history.replace('/admin');
      }
    });
  };

  const {
    isUnique: uniqueEmail,
    errorMessage: errorMessageEmail,
    validateField: validateFieldEmail,
  } = useWithApiValidation('email', validateEmail);
  const {
    isUnique: uniquePhone,
    errorMessage: errorMessagePhone,
    validateField: validateFieldPhone,
  } = useWithApiValidation('phone', validatePhone);

  return (
    <Root
      header={<ComposedHeader decorator="button" />}
      content={
        <Box
          className="flex justify-center items-start"
          sx={{
            width: '100%',
            height: '100%',
          }}
        >
          <FormCardLayout title="Agregar administrador">
            <FormCardLayout.FullColum>
              <ImagePicker
                onAfterUpload={handleLoadImage}
                onDeleteUpload={() => setAvatar(null)}
                baseUri={api.host}
                storeRequest={uploadImageFile}
                formDataParams={imageDefaults}
                maxSize={4000}
              />
            </FormCardLayout.FullColum>
            <FormCardLayout.FullColum>
              <form
                name="adminCreateForm"
                noValidate
                className="flex flex-col justify-center w-full"
                autoComplete="off"
              >
                <div className="flex flex-col">
                  <Controller
                    name="name"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        className="mb-32"
                        label="Nombre"
                        autoFocus
                        type="text"
                        error={!!errors.name}
                        helperText={errors?.name?.message}
                        variant="outlined"
                        required
                        fullWidth
                      />
                    )}
                  />
                  <Controller
                    name="email"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        className="mb-32"
                        label="Correo electrónico"
                        autoFocus
                        type="email"
                        error={!!errors.email || !uniqueEmail}
                        helperText={errors?.email?.message || errorMessageEmail}
                        variant="outlined"
                        required
                        fullWidth
                        onChange={(current) => {
                          field.onChange(current);
                          validateFieldEmail(current);
                        }}
                      />
                    )}
                  />
                  <Grid className="mb-32" spacing={2.5} container>
                    <Grid xs={3} item>
                      <Controller
                        name="phoneCode"
                        control={control}
                        render={({ field }) => (
                          <PhoneCodeSelect
                            {...field}
                            type="text"
                            error={!!errors.phoneCode}
                            helperText={errors?.phoneCode?.message}
                            variant="outlined"
                            required
                            fullWidth
                            inputProps={{
                              style: {
                                height: '5.3rem',
                              },
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid xs={9} item>
                      <Controller
                        name="phone"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Número telefónico"
                            autoFocus
                            type="number"
                            error={!!errors.phone || !uniquePhone}
                            helperText={errors?.phone?.message || errorMessagePhone}
                            variant="outlined"
                            fullWidth
                            onChange={(current) => {
                              field.onChange(current);
                              validateFieldPhone({
                                field: 'phone',
                                value: current.target.value,
                              });
                            }}
                            InputProps={{
                              inputProps: {
                                style: {
                                  WebkitAppearance: 'none',
                                  margin: 0,
                                  MozAppearance: 'textfield',
                                },
                              },
                            }}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>

                  <Controller
                    name="password"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        className="mb-32"
                        label="Contraseña"
                        type="password"
                        error={!!errors.password}
                        helperText={errors?.password?.message}
                        variant="outlined"
                        required
                        fullWidth
                      />
                    )}
                  />
                  <Controller
                    name="password_confirmation"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        className="mb-32"
                        label="Confirmar contraseña"
                        type="password"
                        error={!!errors.password_confirmation}
                        helperText={errors?.password_confirmation?.message}
                        variant="outlined"
                        required
                        fullWidth
                      />
                    )}
                  />
                  <Controller
                    name="status"
                    control={control}
                    render={({ field }) => (
                      <>
                        <FormLabel component="legend">Estatus del administrador</FormLabel>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={field.value}
                              {...field}
                              inputProps={{ 'aria-label': 'Estatus' }}
                            />
                          }
                          label={field.value ? 'Habilitado' : 'Deshabilitado'}
                        />
                      </>
                    )}
                  />
                  <Divider className="mb-8 mt-12">Permisos para administrador</Divider>
                  <Controller
                    name="scopes"
                    control={control}
                    render={({ field }) => (
                      <PermissionsCollection
                        field={field}
                        errors={!!errors.scopes}
                        catalogScopes={catalogScopes}
                      />
                    )}
                  />
                </div>
              </form>
            </FormCardLayout.FullColum>
            <FormCardLayout.FullColum>
              <div>
                <MetadataExpandForm
                  onChange={(metafields) => setMetadata(metafields)}
                  onError={(metaError) => setMetadataError(metaError)}
                />
              </div>
            </FormCardLayout.FullColum>
            <FormCardLayout.RowButton>
              <Button
                onClick={() => history.push('/admin')}
                variant="contained"
                className="w-full md:w-auto"
                color="error"
              >
                Cancelar
              </Button>
              <ButtonWithLoader
                onClick={handleSubmit(handleUpdate)}
                color="success"
                aria-label="Agregar administrador"
                variant="contained"
                disabled={_.isEmpty(dirtyFields) || !isValid || metadataError}
                type="submit"
                isLoading={isLoading}
              >
                Agregar
              </ButtonWithLoader>
            </FormCardLayout.RowButton>
          </FormCardLayout>
        </Box>
      }
    />
  );
};

export default Create;
